import { observer } from 'mobx-react';
import React, { useMemo, useReducer } from 'react';

import { KeyboardArrowDown, SmartToyOutlined } from '@mui/icons-material';
import { Avatar, Button } from '@mui/material';

import useStores from 'hooks/use-stores';
import ProtectedData from 'pages/item/v2/components/ProtectedData';
import ReferenceGroupItem from 'pages/item/v2/components/Related/components/ReferenceGroupItem';
import Utils from 'utils/Utils';

import useStyles from './styles';

interface Props {
    type: string;
    items: {
        id: string;
        title: string;
        published: string;
        bulletinFamily: string;
    }[];
    className?: string;
}

const DEFAULT_LIST_SIZE = 3;

const ReferenceGroup: React.FC<Props> = ({ type, items, className }) => {
    const { classes, cx } = useStyles();
    const { robotTypesStore } = useStores();

    const [isOpen, toggleIsOpen] = useReducer((state) => !state, false);

    const hasMore = items.length > DEFAULT_LIST_SIZE;
    const slice = useMemo(() => (isOpen ? items : items.slice(0, DEFAULT_LIST_SIZE)), [isOpen, items]);
    const bulletinFamily = slice[0]?.bulletinFamily;

    return (
        <div className={cx(classes.root, className)}>
            <div className={classes.summary} id={type}>
                <ProtectedData data={type} rounded>
                    {({ isProtected }) => (
                        <Avatar
                            {...(isProtected
                                ? { children: <SmartToyOutlined color="action" /> }
                                : {
                                      href: `/search?query=type:${type}`,
                                      component: 'a',
                                      src: Utils.getImage(type),
                                      target: '_blank'
                                  })}
                            className={classes.avatar}
                        />
                    )}
                </ProtectedData>
                <div className={classes.text}>
                    <div className={classes.title}>
                        {<ProtectedData data={type}>{robotTypesStore.get(type)}</ProtectedData>}
                    </div>
                    {bulletinFamily ? (
                        <div className={classes.subtitle}>{<ProtectedData data={bulletinFamily} />}</div>
                    ) : null}
                </div>
                {hasMore ? (
                    <Button
                        className={classes.more}
                        color="primary"
                        variant="contained"
                        onClick={toggleIsOpen}
                        endIcon={<KeyboardArrowDown style={{ transform: isOpen ? 'rotate(180deg)' : 'initial' }} />}
                    >
                        {items.length}
                    </Button>
                ) : null}
            </div>
            <div className={classes.list}>
                {slice.map((item, index) => (
                    <ReferenceGroupItem {...item} type={type} key={index} />
                ))}
            </div>
        </div>
    );
};

export default observer(ReferenceGroup);
